<script setup lang="ts">
import { useExtractedDatesStore } from "@/store/extractedDatesStore"
import { useUserStore } from "@/store/userStore"
import { restoreStoreState } from '@/common/helpers/auth'
import { auth } from '@/common/helpers/firebase'
import { onAuthStateChanged, signInWithCustomToken } from 'firebase/auth'
import { onMounted, ref } from "vue"
import { InputPanel, Notification } from "./components"
import Banner from "./components/Banner.vue"
import CalendarSettingModal from "./components/CalendarSettingModal.vue"
import HowToUseModal from "./components/HowToUseModal.vue"

const calendarSettingModal = ref<InstanceType<typeof CalendarSettingModal>>()
const howToUseModal = ref<InstanceType<typeof HowToUseModal>>()
const textGenHost = import.meta.env.VITE_APP_TEXT_GEN_HOST
const isDev = import.meta.env.DEV

const clearStorage = () => {
  localStorage.clear()
  sessionStorage.clear()
  location.reload()
}

onMounted(() => {
  const searchParams = new URLSearchParams(window.location.search)
  if (searchParams.has('token')) {
    restoreStoreState()
    signInWithCustomToken(auth, searchParams.get('token')!).then(async () => {
      window.history.replaceState({}, document.title, "/");
    })
  }

  onAuthStateChanged(auth, async (user) => {
    const userStore = useUserStore()
    if (user) {
      await userStore.setup()
      const extractedDatesStore = useExtractedDatesStore()
      if (userStore.isCalendarScopeAuthorized && extractedDatesStore.hasExtractedDates) {
        extractedDatesStore.refreshAvailability()
      }
    } else {
      userStore.$patch({ userPublicProfile: null })
      const extractedDatesStore = useExtractedDatesStore()
      extractedDatesStore.cachedEvents = []
      extractedDatesStore.extractedDates.forEach(d => {
        d.availability = null
        d.availablePeriods = []
      })
    }
  })
})
</script>

<template>
  <main class="fill-vw-vh">
    <Banner class="top-banner pc-banner" />
    <section class="container lo-v-center">
      <section class="app-content">
        <div class="title-row">
          <h1 class="center-logo"><img src="@/assets/logo-dark.svg" alt="日時の文字を受け取るくん" class="center-logo-img" /></h1>
          <a :href="nitteHost" target="_blank" class="text-gen-link">送るくん <img src="@/assets/external-link-gray.svg" /></a>
        </div>
        <p class="subtitle">受け取った候補日時を貼るだけ<span class="pc-only">、</span><br class="sp-only">そのままカレンダーに。</p>
        <Notification />
        <section class="contents bg-white mg-top-16px">
          <InputPanel @open-how-to-use="howToUseModal?.open()" @open-settings="calendarSettingModal?.open()" />
        </section>
        <HowToUseModal ref="howToUseModal" />
        <CalendarSettingModal ref="calendarSettingModal" />
        <Banner class="top-banner sp-banner" />
        <div class="footer-links">
          <a href="https://site.nitte.app" target="_blank" class="powered-by">Powered by <img src="@/assets/nitte-logo.svg" alt="Nitte" class="nitte-logo" /></a>
        </div>
        <div v-if="isDev" class="dev-toolbar">
          <span class="dev-badge">DEV</span>
          <button class="dev-button" @click="clearStorage">Clear Storage</button>
        </div>
      </section>
    </section>
  </main>
</template>

<style>
@import "@/common/styles/color.css";
@import "@/common/styles/font.css";
@import "@/common/styles/global.css";
@import "@/common/styles/layout.css";
@import "@/common/styles/grid.css";
@import "@/common/styles/margin.css";
@import "@/common/styles/padding.css";
@import "@/common/styles/border.css";
@import "@/common/styles/background.css";
@import "@/common/styles/shadow.css";
@import "@/common/styles/reset.css";
@import "@/common/styles/utility.css";
@import "@/common/styles/z_index.css";
@import "@/common/styles/modal.css";

html, body {
  background-color: var(--bg-screen);
}
#app {
  height: auto;
  min-height: 100vh;
}
</style>

<style scoped>
.title-row {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 48px;
  position: relative;
}
.center-logo {
  margin: 0;
  line-height: 0;
}
.center-logo-img {
  height: 22px;
}
.footer-links {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 16px;
  position: relative;
}
.powered-by {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8125rem;
  font-weight: 400;
  color: var(--font-gray-2);
  text-decoration: none;
}
.powered-by:hover {
  color: var(--font-gray-2);
}
.nitte-logo {
  height: 16px;
}
.app-content {
  max-width: 640px;
  width: 100%;
}
.text-gen-link {
  position: absolute;
  right: 8px;
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 0.8125rem;
  color: var(--font-gray-2);
  text-decoration: none;
  transition: color 0.15s;
}
.text-gen-link:hover {
  color: var(--font-gray-1);
}
.subtitle {
  text-align: center;
  font-size: 0.9375rem;
  color: var(--font-gray-2);
  margin: 6px 0 0;
  padding: 0 32px;
}
.sp-only {
  display: none;
}
.pc-only {
  display: inline;
}
@media (max-width: 800px) {
  .sp-only {
    display: inline;
  }
  .pc-only {
    display: none;
  }
}
.dev-toolbar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
}
.dev-badge {
  font-size: 0.625rem;
  font-weight: 700;
  color: white;
  background: #e53e3e;
  padding: 2px 6px;
  border-radius: 4px;
}
.dev-button {
  font-size: 0.6875rem;
  color: var(--font-gray-3);
  background: none;
  border: 1px solid #d1d5db;
  padding: 3px 8px;
  border-radius: 4px;
  cursor: pointer;
}
.dev-button:hover {
  color: var(--font-gray-1);
  border-color: #9ca3af;
}
.top-banner {
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
  padding: 12px 16px 0;
  box-sizing: border-box;
}
.pc-banner {
  display: block;
}
.sp-banner {
  display: none;
}
@media (max-width: 800px) {
  .pc-banner {
    display: none;
  }
  .sp-banner {
    display: block;
    padding: 0;
    margin-top: 16px;
  }
}
</style>
